import { createAction, props } from '@ngrx/store';
import { SaveDraftItem } from '@shared/components/save-draft-modal/save-draft-modal.model';
import { ExperienceType } from '@shared/models/draft';
import type { RetrieveDraftItem } from '@shared/services/draft/draft.service';

export const SaveNewDraft = createAction(
  '[Draft] Save New Draft',
  props<{ experienceType: ExperienceType }>(),
);
export const SaveNewDraftSuccess = createAction(
  '[Draft] Save New Draft Success',
  props<{
    draftId: string;
    expDate: string;
    baseProcessTrackingId: string;
    draftSequence: number;
  }>(),
);
export const SaveNewDraftFailure = createAction('[Draft] Save New Draft Failure');

export const RetrieveDrafts = createAction(
  '[Draft] Retrieve Drafts',
  props<{ experienceType: ExperienceType }>(),
);
export const RetrieveDraftsSuccess = createAction(
  '[Draft] Retrieve Drafts Success',
  props<{ drafts: SaveDraftItem[]; rawDrafts: RetrieveDraftItem[] }>(),
);
export const RetrieveDraftsFailure = createAction('[Draft] Retrieve Drafts Failure');
export const UpdateDraft = createAction('[Draft] Update Draft');
export const UpdateDraftSuccess = createAction('[Draft] Update Draft Success');
export const UpdateDraftFailure = createAction('[Draft] Update Draft Failure');

export const DeleteDrafts = createAction(
  '[Draft] Delete Drafts',
  props<{ ids: string[]; skipRetrieve?: boolean; skipDocumentCleanup?: boolean }>(),
);
export const DeleteDraftsStarted = createAction('[Draft] Delete Drafts Started');
export const DeleteDraftsSuccess = createAction('[Draft] Delete Drafts Success');
export const DeleteDraftsFailure = createAction('[Draft] Delete Drafts Failure');
export const HydrateDraft = createAction('[Draft] Hydrate Draft', props<{ draftId: string }>());
export const HydrateDraftSuccess = createAction(
  '[Draft] Hydrate Draft Success',
  props<{
    draftId: string;
    baseProcessTrackingId: string;
    draftSequence: number;
  }>(),
);
export const HydrateDraftFailure = createAction('[Draft] Hydrate Draft Failure');
export const ActiveDraftChanged = createAction(
  '[Draft] Active Draft Changed',
  props<{ transactionId: string }>(),
);
export const ActiveDraftChangedSuccess = createAction(
  '[Draft] Active Draft Changed Success',
  props<{ draftId: string; draftSequence: number }>(),
);
export const ActiveDraftChangedFailure = createAction('[Draft] Active Draft Changed Failure');

/**
 * Separate actions because they must NOT converge on one state: declining unblocks draft
 * creation, accepting must not. `draftId` is null until HydrateDraftSuccess, so a shared
 * "decision made" state orphaned a second draft on every resume.
 */
export const DraftResumeAccepted = createAction('[Draft] Resume Accepted');
export const DraftResumeDeclined = createAction('[Draft] Resume Declined');
export const StaleDraftDiscarded = createAction('[Draft] Stale Draft Discarded');

// Lives in the store, not the component — the page gets destroyed on navigation, the failure doesn't.
export const DraftLookupFailureAcknowledged = createAction('[Draft] Lookup Failure Acknowledged');

export const SaveDraftOnQuit = createAction('[Draft] Save Draft On Quit');

export const MinorFlowEntered = createAction('[Minor Transfer] Flow Entered');

/**
 * The client is being turned away from the minor flow, so a draft they saved earlier can never
 * be reached again.
 *
 * MinorEligibilityGuard dispatches this immediately before routing to the ineligible page, and
 * that page's ClearStateAndStorage resets the flow while the draft lookup is still in flight.
 */
export const StaleMinorDraftCleanupRequested = createAction(
  '[Minor Transfer] Stale Draft Cleanup Requested',
  props<{ eligibleAccountIds: string[] }>(),
);

/** Treated like RetrieveDraftsFailure by the reducer, so a hung lookup fails open the same way. */
export const MinorDraftLookupTimedOut = createAction('[Draft] Minor Lookup Timed Out');
